
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Edit2, Trash2, Package, Camera, Filter, Monitor, Battery, 
  Anchor, HardDrive, Wrench, Lightbulb, Grip, MoreVertical 
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const categoryIcons: Record<string, any> = {
  "Cameras": Camera,
  "Lenses": Camera,
  "Filters": Filter,
  "Monitoring": Monitor,
  "Power": Battery,
  "Camera Support": Anchor,
  "Media": HardDrive,
  "Camera AKS": Wrench,
  "Lighting Fixtures": Lightbulb,
  "Grip": Grip,
  "Specialty Items": Package,
};

interface InventoryCardProps {
  item: any;
  onEdit: (item: any) => void;
  onDelete: (id: string) => void;
}

export function InventoryCard({ item, onEdit, onDelete }: InventoryCardProps) {
  const { currency } = useCurrency();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const IconComponent = categoryIcons[item.category] || Package;

  return (
    <>
      <Card className="group relative overflow-hidden hover:shadow-lg active:scale-[0.98] transition-all duration-200 bg-card/50 backdrop-blur-sm border-white/10 rounded-xl">
        <div className="p-5 space-y-4">
          {/* Header with icon and full name */}
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 bg-primary/15 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-lg leading-tight break-words text-white mb-2">
                {item.name}
              </h3>
              <span className="text-sm text-white/70 bg-white/5 px-3 py-1 rounded-full">
                {item.category}
              </span>
            </div>
            
            {/* Actions button - larger touch target for mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 p-0 rounded-xl hover:bg-white/10 shrink-0"
                >
                  <MoreVertical className="h-5 w-5 text-white/70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 bg-background/95 backdrop-blur-md border-white/10 rounded-xl">
                <DropdownMenuItem onClick={() => onEdit(item)} className="cursor-pointer h-11 text-base rounded-lg">
                  <Edit2 className="h-4 w-4 mr-3 text-muted-foreground" />
                  Edit Item
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowDeleteDialog(true)} 
                  className="cursor-pointer text-destructive focus:text-destructive h-11 text-base rounded-lg"
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  Delete Item
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Details with better spacing */}
          <div className="pt-4 border-t border-border/20 flex justify-between items-center">
            <div className="text-center">
              <span className="text-white/60 text-sm block mb-1">Quantity</span>
              <span className="font-bold text-white text-xl">{item.quantity}</span>
            </div>
            <div className="h-12 border-r border-border/20"></div>
            <div className="text-center">
              <span className="text-white/60 text-sm block mb-1">Daily Rate</span>
              <span className="font-bold text-white text-xl">{formatCurrency(item.cost_per_unit, currency)}</span>
            </div>
          </div>
        </div>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass-card border-white/10 max-w-[90%] w-[380px] mx-4 rounded-xl">
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle className="text-xl font-semibold">Delete Item</AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-relaxed text-white/80">
              This will permanently delete "{item.name}" from your inventory.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-3 pt-4">
            <AlertDialogCancel className="bg-background/60 hover:bg-background/80 border-white/10 h-12 rounded-xl font-medium">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(item.id);
                setShowDeleteDialog(false);
              }}
              className="bg-destructive/90 hover:bg-destructive h-12 rounded-xl font-medium"
            >
              Delete Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
